/**
 * Relatórios > Financeiro > Despesas.
 *
 * Mensal: despesas do mês por Grupo DRE → Categoria → Subcategoria com
 * drill-down de lançamentos, ranking das maiores despesas e evolução de
 * 12 meses com multi-select de grupos.
 * Anual: tabela expansível Grupo → Categoria → Subcategoria com uma coluna
 * por mês (ano atual ou últimos 12 meses) e coluna Total.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronRight,
  LineChart as LineChartIcon,
  ListOrdered,
  Receipt,
} from 'lucide-react';
import { CreditCard as CreditCardIcon } from 'lucide-react';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { clsx } from 'clsx';
import financialReportsService from '@services/financialReportsService';
import type {
  ExpenseTransaction,
  ExpensesAnnualGroup,
  ExpensesAnnualReport,
  ExpensesAnnualRow,
  ExpensesHistory,
  ExpensesReport,
} from '@/types/financialReports';
import { formatCurrencyBRL, formatDateBR } from '@pages/financial/financialUtils';
import {
  ErrorBox,
  LoadingSpinner,
  ModalShell,
  SummaryCard,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
  selectCls,
} from '@pages/financial/financialShared';
import {
  BasisToggle,
  ChartTooltip,
  CompanySelect,
  DeltaBadge,
  FutureCompetenceNotice,
  ProjectedBanner,
  SERIES_COLORS,
  SourceBadge,
  getMonthOptions,
  getYearOptions,
  kFormatter,
  useCompanies,
} from './financialReportsShared';
import { useUserPreferences, PREF_FIN_REPORT_EXPENSES } from '@hooks/useUserPreferences';
import { useIsMobile } from '@hooks/useIsMobile';
import { financialService } from '@services/financialService';
import type { CreditCardProjection } from '@/types/financial';
import { CreditCardProjectionMatrix } from '@pages/financial/CreditCardProjectionMatrix';
import {
  currentCompetence,
  defaultTopGroups,
  emptyPeriodMessage,
  formatPct,
  historyToChartData,
  isFutureCompetence,
  monthLabelPtBR,
  pctDelta,
  revenueSharePct,
} from './financialReportsUtils';
import type { ReportBasis } from '@/types/financialReports';

// ─── Modal de drill-down (lançamentos da categoria) ─────────────

const TransactionsModal: React.FC<{
  year: number;
  month: number;
  companyId: number | null;
  erpId: number;
  title: string;
  basis: ReportBasis;
  onClose: () => void;
}> = ({ year, month, companyId, erpId, title, basis, onClose }) => {
  const [rows, setRows] = useState<ExpenseTransaction[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await financialReportsService.getExpenseTransactions(year, month, erpId, companyId, basis));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar lançamentos');
    } finally {
      setLoading(false);
    }
  }, [year, month, erpId, companyId, basis]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const total = (rows ?? []).reduce((s, t) => s + t.amount, 0);

  return (
    <ModalShell
      title={`Lançamentos — ${title}`}
      icon={<Receipt size={18} className="text-emerald-600" />}
      onClose={onClose}
      maxWidth="max-w-4xl"
      footer={
        <button type="button" className={secondaryBtnCls} onClick={onClose}>
          Fechar
        </button>
      }
    >
      {loading && <LoadingSpinner />}
      {error && <ErrorBox message={error} onRetry={fetchRows} />}
      {!loading && !error && rows && (
        rows.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">Nenhum lançamento encontrado no período.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Fonte</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Data</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Descrição</th>
                  <th className="text-right py-2 px-3 font-semibold text-slate-600">Valor</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Conta / Cartão</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Mês Fatura</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(t => (
                  // Um pai rateado vem em N linhas com o mesmo transactionId
                  // (uma por categoria) — a parte é o que identifica a linha.
                  <tr key={`${t.source}-${t.allocationId ?? t.transactionId}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3"><SourceBadge source={t.source} /></td>
                    <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{formatDateBR(t.date)}</td>
                    <td className="py-2 px-3 text-slate-700">
                      {t.description}
                      {t.allocationOf != null && (
                        <span className="block text-xs text-slate-500">
                          parte de {formatCurrencyBRL(Math.abs(t.allocationOf))} · rateado
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right font-medium text-slate-900 whitespace-nowrap">
                      {formatCurrencyBRL(Math.abs(t.amount))}
                    </td>
                    <td className="py-2 px-3 text-slate-700">{t.accountOrCardName}</td>
                    <td className="py-2 px-3 text-slate-500">{t.invoiceMonth ? monthLabelPtBR(t.invoiceMonth) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold">
                  <td colSpan={3} className="py-2 px-3 text-slate-700">Total ({rows.length} lançamentos)</td>
                  <td className="py-2 px-3 text-right text-slate-900">{formatCurrencyBRL(Math.abs(total))}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </ModalShell>
  );
};

// ─── Visão anual — tabela Grupo → Categoria → Subcategoria ──────

/** Classe base da coluna de nome (sticky à esquerda no scroll horizontal). */
const stickyNameCellCls = 'sticky left-0 z-10 py-2 px-3 min-w-[150px] sm:min-w-[240px]';

/** Célula de valor da tabela anual — 0 vira "–" para legibilidade. */
const AnnualMoneyCell: React.FC<{ value: number; bold?: boolean; muted?: boolean }> = ({ value, bold, muted }) =>
  value === 0 ? (
    <td className="py-2 px-3 text-right text-slate-300">–</td>
  ) : (
    <td
      className={clsx(
        'py-2 px-3 text-right whitespace-nowrap',
        bold ? 'font-semibold text-slate-900' : muted ? 'text-slate-500' : 'text-slate-700',
      )}
    >
      {formatCurrencyBRL(value)}
    </td>
  );

const AnnualSubcategoryRow: React.FC<{ row: ExpensesAnnualRow }> = ({ row }) => (
  <tr className="border-b border-slate-100 hover:bg-slate-50">
    <td className={clsx(stickyNameCellCls, 'bg-white pl-16 text-slate-500')}>{row.description}</td>
    {row.monthTotals.map((v, i) => (
      <AnnualMoneyCell key={i} value={v} muted />
    ))}
    <AnnualMoneyCell value={row.total} bold />
  </tr>
);

const AnnualCategoryRows: React.FC<{
  category: ExpensesAnnualRow;
  expanded: boolean;
  onToggle: () => void;
}> = ({ category, expanded, onToggle }) => {
  const hasSubs = category.subcategories.length > 0;
  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50">
        <td className={clsx(stickyNameCellCls, 'bg-white pl-10')}>
          <span className="inline-flex items-center gap-1">
            {hasSubs ? (
              <button
                type="button"
                onClick={onToggle}
                className="text-slate-400 hover:text-slate-600"
                title={expanded ? 'Recolher subcategorias' : 'Expandir subcategorias'}
              >
                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            ) : (
              <span className="w-[14px]" />
            )}
            <span className="text-slate-700">{category.description}</span>
          </span>
        </td>
        {category.monthTotals.map((v, i) => (
          <AnnualMoneyCell key={i} value={v} />
        ))}
        <AnnualMoneyCell value={category.total} bold />
      </tr>
      {expanded && category.subcategories.map(sub => <AnnualSubcategoryRow key={sub.erpId} row={sub} />)}
    </>
  );
};

const AnnualGroupRows: React.FC<{
  group: ExpensesAnnualGroup;
  expanded: boolean;
  onToggle: () => void;
  expandedCategories: Set<number>;
  onToggleCategory: (erpId: number) => void;
}> = ({ group, expanded, onToggle, expandedCategories, onToggleCategory }) => (
  <>
    <tr className="border-b border-slate-100 bg-slate-50 hover:bg-slate-100">
      <td className={clsx(stickyNameCellCls, 'bg-slate-50')}>
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex items-center gap-2 font-semibold text-slate-800 hover:text-emerald-700"
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {group.name}
        </button>
      </td>
      {group.monthTotals.map((v, i) => (
        <AnnualMoneyCell key={i} value={v} />
      ))}
      <AnnualMoneyCell value={group.total} bold />
    </tr>
    {expanded &&
      group.categories.map(cat => (
        <AnnualCategoryRows
          key={cat.erpId}
          category={cat}
          expanded={expandedCategories.has(cat.erpId)}
          onToggle={() => onToggleCategory(cat.erpId)}
        />
      ))}
  </>
);

const ExpensesAnnualTable: React.FC<{
  report: ExpensesAnnualReport;
  year: number;
  basis: ReportBasis;
  onSwitchToProjected: () => void;
}> = ({ report, year, basis, onSwitchToProjected }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  const toggle = (set: React.Dispatch<React.SetStateAction<Set<number>>>, id: number) =>
    set(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const competenceRef = currentCompetence();

  if (report.groups.length === 0) {
    if (basis === 'realizado' && isFutureCompetence(year, 1, competenceRef)) {
      return (
        <FutureCompetenceNotice year={year} month={null} onSwitchToProjected={onSwitchToProjected} />
      );
    }
    return (
      <p className="text-sm text-slate-500 py-6 text-center">
        {emptyPeriodMessage(year, null, basis, competenceRef, 'Nenhuma despesa encontrada no período.')}
      </p>
    );
  }

  // Índice da primeira coluna projetada — a fronteira entre o que já
  // aconteceu e o que ainda vai acontecer.
  const projectedIdx = report.projectedFrom ? report.months.indexOf(report.projectedFrom) : -1;
  const projectedCol = (i: number) => (projectedIdx >= 0 && i >= projectedIdx ? 'bg-amber-50/60' : undefined);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className={clsx(stickyNameCellCls, 'bg-white text-left font-semibold text-slate-600')}>
              Grupo / Categoria
            </th>
            {report.months.map((m, i) => (
              <th
                key={m}
                className={clsx(
                  'text-right py-2 px-3 font-semibold text-slate-600 whitespace-nowrap',
                  projectedCol(i),
                )}
                title={projectedIdx >= 0 && i >= projectedIdx ? 'Competência projetada' : undefined}
              >
                {monthLabelPtBR(m)}
                {projectedIdx >= 0 && i === projectedIdx && (
                  <span className="block text-[10px] font-normal text-amber-700">projetado →</span>
                )}
              </th>
            ))}
            <th className="text-right py-2 px-3 font-bold text-slate-700">Total</th>
          </tr>
        </thead>
        <tbody>
          {report.groups.map(group => (
            <AnnualGroupRows
              key={group.dreGroupId}
              group={group}
              expanded={expandedGroups.has(group.dreGroupId)}
              onToggle={() => toggle(setExpandedGroups, group.dreGroupId)}
              expandedCategories={expandedCategories}
              onToggleCategory={erpId => toggle(setExpandedCategories, erpId)}
            />
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-slate-50 font-semibold border-t border-slate-200">
            <td className={clsx(stickyNameCellCls, 'bg-slate-50 text-slate-700')}>Total</td>
            {report.monthTotals.map((v, i) => (
              <AnnualMoneyCell key={i} value={v} bold />
            ))}
            <td className="py-2 px-3 text-right font-bold text-slate-900 whitespace-nowrap">
              {formatCurrencyBRL(report.grandTotal)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ─── View principal ─────────────────────────────────────────────

type ExpensesMode = 'mensal' | 'anual';
type AnnualPeriodMode = 'year' | 'last12';

interface ExpensesPrefs extends Record<string, unknown> {
  mode?: ExpensesMode;
  annual_mode?: AnnualPeriodMode;
  company_id?: number;
  group_ids?: number[];
}

interface DrilldownTarget {
  erpId: number;
  title: string;
}

export const ExpensesReportView: React.FC = () => {
  const isMobile = useIsMobile();
  const now = new Date();
  const [mode, setMode] = useState<ExpensesMode>('mensal');
  const [annualMode, setAnnualMode] = useState<AnnualPeriodMode>('year');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [companyId, setCompanyId] = useState<number | null>(null);
  /**
   * Realizado × Projetado. Deliberadamente FORA das preferências salvas: o
   * basis não é um filtro, é uma reinterpretação do período. Persistir
   * "projetado" faria o usuário reabrir a tela dias depois lendo número
   * projetado como se fosse realizado — o engano que esta tela corrige.
   */
  const [basis, setBasis] = useState<ReportBasis>('realizado');
  const { companies, companiesError } = useCompanies();

  const [report, setReport] = useState<ExpensesReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [annualReport, setAnnualReport] = useState<ExpensesAnnualReport | null>(null);
  const [annualLoading, setAnnualLoading] = useState(false);
  const [annualError, setAnnualError] = useState<string | null>(null);

  const [history, setHistory] = useState<ExpensesHistory | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Projeção de cartões (parcelas × competências futuras): seção própria após
  // o gráfico de 12 meses. Falha aqui não derruba o resto do relatório.
  const [cardProjection, setCardProjection] = useState<CreditCardProjection | null>(null);
  const [cardProjectionLoading, setCardProjectionLoading] = useState(false);
  const [cardProjectionError, setCardProjectionError] = useState<string | null>(null);

  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [drilldown, setDrilldown] = useState<DrilldownTarget | null>(null);
  /** null = ainda não inicializado (aplica o default top-5 no primeiro load). */
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[] | null>(null);

  const { prefs, loaded: prefsLoaded, save: savePrefs } = useUserPreferences<ExpensesPrefs>(PREF_FIN_REPORT_EXPENSES);
  const [filtersReady, setFiltersReady] = useState(false);

  // Restaura os filtros salvos antes da primeira busca (o período fica no mês corrente)
  useEffect(() => {
    if (!prefsLoaded) return;
    if (prefs?.mode === 'mensal' || prefs?.mode === 'anual') setMode(prefs.mode);
    if (prefs?.annual_mode === 'year' || prefs?.annual_mode === 'last12') setAnnualMode(prefs.annual_mode);
    if (prefs?.company_id != null) setCompanyId(prefs.company_id);
    if (prefs?.group_ids && prefs.group_ids.length > 0) setSelectedGroupIds(prefs.group_ids);
    setFiltersReady(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsLoaded]);

  useEffect(() => {
    if (!filtersReady) return;
    savePrefs({
      mode,
      annual_mode: annualMode,
      company_id: companyId ?? undefined,
      group_ids: selectedGroupIds ?? undefined,
    });
  }, [filtersReady, mode, annualMode, companyId, selectedGroupIds, savePrefs]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await financialReportsService.getExpensesReport(year, month, companyId, basis);
      setReport(data);
      setSelectedGroupIds(prev => prev ?? defaultTopGroups(data.groups, 5));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar relatório de despesas');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [year, month, companyId, basis]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      setHistory(await financialReportsService.getExpensesHistory(12, companyId));
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : 'Erro ao carregar histórico de despesas');
      setHistory(null);
    } finally {
      setHistoryLoading(false);
    }
  }, [companyId]);

  const fetchCardProjection = useCallback(async () => {
    setCardProjectionLoading(true);
    setCardProjectionError(null);
    try {
      setCardProjection(await financialService.getCreditCardProjection());
    } catch (err) {
      setCardProjectionError(err instanceof Error ? err.message : 'Erro ao carregar a projeção de cartões');
      setCardProjection(null);
    } finally {
      setCardProjectionLoading(false);
    }
  }, []);

  useEffect(() => {
    // Uma vez por visita, junto com o modo mensal (onde a seção aparece).
    if (filtersReady && mode === 'mensal' && cardProjection === null && !cardProjectionLoading && !cardProjectionError) {
      fetchCardProjection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady, mode, fetchCardProjection]);

  const fetchAnnual = useCallback(async () => {
    setAnnualLoading(true);
    setAnnualError(null);
    try {
      setAnnualReport(await financialReportsService.getExpensesAnnual(annualMode, year, companyId, basis));
    } catch (err) {
      setAnnualError(err instanceof Error ? err.message : 'Erro ao carregar visão anual de despesas');
      setAnnualReport(null);
    } finally {
      setAnnualLoading(false);
    }
  }, [annualMode, year, companyId, basis]);

  useEffect(() => {
    if (filtersReady && mode === 'mensal') fetchReport();
  }, [fetchReport, mode, filtersReady]);

  useEffect(() => {
    if (filtersReady && mode === 'mensal') fetchHistory();
  }, [fetchHistory, mode, filtersReady]);

  useEffect(() => {
    if (filtersReady && mode === 'anual') fetchAnnual();
  }, [fetchAnnual, mode, filtersReady]);

  const toggleSet = (set: Set<number>, id: number): Set<number> => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  // Chips do multi-select: todos os grupos do histórico, ordenados pelo
  // valor do mês corrente (maiores primeiro) — cor estável por posição.
  const chipGroups = useMemo(() => {
    if (!history) return [] as { id: number; label: string; color: string }[];
    const totalsById = new Map<number, number>();
    for (const g of report?.groups ?? []) totalsById.set(g.dreGroupId, Math.abs(g.total));
    return Object.entries(history.labels)
      .map(([key, label]) => ({ id: Number(key), label }))
      .sort((a, b) => (totalsById.get(b.id) ?? 0) - (totalsById.get(a.id) ?? 0) || a.label.localeCompare(b.label))
      .map((g, idx) => ({ ...g, color: SERIES_COLORS[idx % SERIES_COLORS.length] }));
  }, [history, report]);

  const effectiveSelection = selectedGroupIds ?? [];
  const chartData = history ? historyToChartData(history.series, history.labels, effectiveSelection) : [];
  const selectedChips = chipGroups.filter(g => effectiveSelection.includes(g.id));

  const revenueShare = report
    ? revenueSharePct(report.totalExpenses, report.totalRevenue, report.revenueAvailable)
    : null;
  const competenceRef = currentCompetence();
  // O aviso vale quando a competência é futura E o modo é realizado — nesse
  // caso a API devolve vazio por construção, e a mensagem genérica não
  // explicaria o porquê.
  const futureInRealized =
    basis === 'realizado' &&
    (mode === 'mensal'
      ? isFutureCompetence(year, month, competenceRef)
      : annualMode === 'year' && isFutureCompetence(year, 1, competenceRef));

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Visão</label>
            <div className="inline-flex w-full sm:w-auto rounded-lg border border-slate-300 overflow-hidden">
              {(['mensal', 'anual'] as ExpensesMode[]).map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={clsx(
                    'flex-1 sm:flex-none px-4 py-2 text-sm font-medium transition-colors',
                    mode === m ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {m === 'mensal' ? 'Mensal' : 'Anual'}
                </button>
              ))}
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Base</label>
            <BasisToggle value={basis} onChange={setBasis} />
          </div>
          {mode === 'mensal' && (
            <div className="w-full sm:w-auto">
              <label className={labelCls}>Mês</label>
              <select className={`${selectCls} w-full sm:w-32`} value={month} onChange={e => setMonth(Number(e.target.value))}>
                {getMonthOptions({ year }).map(o => (
                  <option key={o.value} value={o.value}>
                    {o.isFuture ? `${o.label} (futuro)` : o.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {mode === 'anual' && (
            <div className="w-full sm:w-auto">
              <label className={labelCls}>Período</label>
              <select
                className={`${selectCls} w-full sm:w-44`}
                value={annualMode}
                onChange={e => setAnnualMode(e.target.value as AnnualPeriodMode)}
              >
                <option value="year">Ano atual</option>
                <option value="last12">Últimos 12 meses</option>
              </select>
            </div>
          )}
          {(mode === 'mensal' || annualMode === 'year') && (
            <div className="w-full sm:w-auto">
              <label className={labelCls}>Ano</label>
              <select className={`${selectCls} w-full sm:w-28`} value={year} onChange={e => setYear(Number(e.target.value))}>
                {getYearOptions().map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          )}
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Empresa</label>
            <CompanySelect companies={companies} value={companyId} onChange={setCompanyId} />
          </div>
          <button
            onClick={mode === 'mensal' ? fetchReport : fetchAnnual}
            disabled={mode === 'mensal' ? loading : annualLoading}
            className={`${primaryBtnCls} w-full sm:w-auto justify-center`}
          >
            <Calendar size={16} />
            {(mode === 'mensal' ? loading : annualLoading) ? 'Carregando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {companiesError && <ErrorBox message={companiesError} />}

      {basis === 'projetado' && <ProjectedBanner />}

      {/* Visão anual */}
      {mode === 'anual' && (
        <>
          {annualError && <ErrorBox message={annualError} onRetry={fetchAnnual} />}
          {annualLoading && <LoadingSpinner />}
          {!annualLoading && !annualError && annualReport && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <h3 className="text-base font-semibold text-slate-800 mb-3">
                Despesas por Grupo DRE — {annualMode === 'year' ? `Ano ${year}` : 'Últimos 12 meses'}
              </h3>
              <ExpensesAnnualTable
                report={annualReport}
                year={annualMode === 'year' ? year : currentCompetence().year}
                basis={basis}
                onSwitchToProjected={() => setBasis('projetado')}
              />
            </div>
          )}
        </>
      )}

      {mode === 'mensal' && error && <ErrorBox message={error} onRetry={fetchReport} />}
      {mode === 'mensal' && loading && <LoadingSpinner />}

      {mode === 'mensal' && !loading && !error && report && (
        <>
          {/* Cards de resumo */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SummaryCard
              label="Total de Despesas"
              value={formatCurrencyBRL(report.totalExpenses)}
              valueClassName="text-red-600"
            />
            <SummaryCard
              label="% sobre a Receita"
              value={revenueShare === null ? '—' : formatPct(revenueShare)}
              hint={
                report.revenueAvailable
                  ? undefined
                  : 'A receita não é projetada — ela vem de notas fiscais emitidas, que não existem para uma competência futura.'
              }
            />
            {report.unmappedTotal > 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl shadow-sm p-4">
                <p className="text-sm text-amber-700 flex items-center gap-1">
                  <AlertTriangle size={14} /> Despesas não mapeadas
                </p>
                <p className="text-xl font-bold mt-1 text-amber-700">{formatCurrencyBRL(report.unmappedTotal)}</p>
                <p className="text-xs text-amber-600 mt-1">Lançamentos sem grupo DRE — revise o plano de contas.</p>
              </div>
            ) : (
              <SummaryCard label="Despesas não mapeadas" value={formatCurrencyBRL(0)} valueClassName="text-slate-400" />
            )}
          </div>

          {/* Tabela expansível Grupo DRE → Categoria → Subcategoria */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <h3 className="text-base font-semibold text-slate-800 mb-3">Despesas por Grupo DRE</h3>
            {report.groups.length === 0 ? (
              futureInRealized ? (
                <FutureCompetenceNotice
                  year={year}
                  month={month}
                  onSwitchToProjected={() => setBasis('projetado')}
                />
              ) : (
                <p className="text-sm text-slate-500 py-6 text-center">
                  {emptyPeriodMessage(year, month, basis, competenceRef, 'Nenhuma despesa encontrada no período.')}
                </p>
              )
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Grupo / Categoria</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Total</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">vs Mês Anterior</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">vs Ano Anterior</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">% do Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.groups.map(group => {
                      const groupExpanded = expandedGroups.has(group.dreGroupId);
                      return (
                        <React.Fragment key={group.dreGroupId}>
                          <tr className="border-b border-slate-100 bg-slate-50/50 hover:bg-slate-50">
                            <td className="py-2 px-3">
                              <button
                                type="button"
                                onClick={() => setExpandedGroups(s => toggleSet(s, group.dreGroupId))}
                                className="inline-flex items-center gap-2 font-semibold text-slate-800 hover:text-emerald-700"
                              >
                                {groupExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                {group.name}
                              </button>
                            </td>
                            <td className="py-2 px-3 text-right font-semibold text-slate-900">{formatCurrencyBRL(group.total)}</td>
                            <td className="py-2 px-3 text-right">
                              <DeltaBadge pct={pctDelta(group.total, group.prevMonthTotal)} positiveIsGood={false} />
                            </td>
                            <td className="py-2 px-3 text-right">
                              <DeltaBadge pct={pctDelta(group.total, group.samePeriodLastYearTotal)} positiveIsGood={false} />
                            </td>
                            <td className="py-2 px-3 text-right text-slate-600">{formatPct(group.sharePct)}</td>
                          </tr>
                          {groupExpanded &&
                            group.categories.map(cat => {
                              const catExpanded = expandedCategories.has(cat.erpId);
                              const hasSubs = cat.subcategories.length > 0;
                              return (
                                <React.Fragment key={cat.erpId}>
                                  <tr className="border-b border-slate-100 hover:bg-slate-50">
                                    <td className="py-2 px-3 pl-4 sm:pl-10">
                                      <span className="inline-flex items-center gap-1">
                                        {hasSubs ? (
                                          <button
                                            type="button"
                                            onClick={() => setExpandedCategories(s => toggleSet(s, cat.erpId))}
                                            className="text-slate-400 hover:text-slate-600"
                                            title={catExpanded ? 'Recolher subcategorias' : 'Expandir subcategorias'}
                                          >
                                            {catExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                          </button>
                                        ) : (
                                          <span className="w-[14px]" />
                                        )}
                                        <button
                                          type="button"
                                          onClick={() => setDrilldown({ erpId: cat.erpId, title: cat.description })}
                                          className="text-slate-700 hover:text-emerald-700 hover:underline text-left"
                                          title="Ver lançamentos"
                                        >
                                          {cat.description}
                                        </button>
                                      </span>
                                    </td>
                                    <td className="py-2 px-3 text-right text-slate-700">{formatCurrencyBRL(cat.total)}</td>
                                    <td colSpan={3} />
                                  </tr>
                                  {catExpanded &&
                                    cat.subcategories.map(sub => (
                                      <tr key={sub.erpId} className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="py-1.5 px-3 pl-6 sm:pl-16">
                                          <button
                                            type="button"
                                            onClick={() => setDrilldown({ erpId: sub.erpId, title: sub.description })}
                                            className="text-slate-500 hover:text-emerald-700 hover:underline text-left"
                                            title="Ver lançamentos"
                                          >
                                            {sub.description}
                                          </button>{' '}
                                          <span className="text-xs text-slate-400">({sub.transactionCount})</span>
                                        </td>
                                        <td className="py-1.5 px-3 text-right text-slate-500">{formatCurrencyBRL(sub.total)}</td>
                                        <td colSpan={3} />
                                      </tr>
                                    ))}
                                </React.Fragment>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-semibold">
                      <td className="py-2 px-3 text-slate-700">Total</td>
                      <td className="py-2 px-3 text-right text-slate-900">{formatCurrencyBRL(report.totalExpenses)}</td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Ranking maiores despesas */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <ListOrdered size={18} className="text-emerald-600" />
              <h3 className="text-base font-semibold text-slate-800">Maiores Despesas do Mês</h3>
            </div>
            {report.topExpenses.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">Nenhuma despesa no período.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">#</th>
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Categoria</th>
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Grupo DRE</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Total</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">% do Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.topExpenses.map((t, idx) => (
                      <tr key={t.erpId} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-3 text-slate-500">{idx + 1}º</td>
                        <td className="py-2 px-3">
                          <button
                            type="button"
                            onClick={() => setDrilldown({ erpId: t.erpId, title: t.description })}
                            className="text-slate-700 hover:text-emerald-700 hover:underline text-left"
                            title="Ver lançamentos"
                          >
                            {t.description}
                          </button>
                        </td>
                        <td className="py-2 px-3 text-slate-500">{t.dreGroupName}</td>
                        <td className="py-2 px-3 text-right font-medium text-slate-900">{formatCurrencyBRL(t.total)}</td>
                        <td className="py-2 px-3 text-right text-slate-600">{formatPct(t.sharePct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Evolução 12 meses */}
      {mode === 'mensal' && (
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <LineChartIcon size={18} className="text-emerald-600" />
          <h3 className="text-base font-semibold text-slate-800">Evolução — Últimos 12 Meses</h3>
        </div>

        {historyError && <ErrorBox message={historyError} onRetry={fetchHistory} />}
        {historyLoading && <LoadingSpinner />}

        {!historyLoading && !historyError && history && (
          chipGroups.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">Sem histórico de despesas.</p>
          ) : (
            <>
              {/* Multi-select de grupos (chips) */}
              <div className="flex flex-wrap gap-2 mb-4">
                {chipGroups.map(chip => {
                  const selected = effectiveSelection.includes(chip.id);
                  return (
                    <button
                      key={chip.id}
                      type="button"
                      onClick={() =>
                        setSelectedGroupIds(prev =>
                          [...toggleSet(new Set(prev ?? []), chip.id)],
                        )
                      }
                      className={clsx(
                        'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                        selected ? 'text-white' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50',
                      )}
                      style={selected ? { backgroundColor: chip.color, borderColor: chip.color } : undefined}
                    >
                      {chip.label}
                    </button>
                  );
                })}
              </div>

              {selectedChips.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">Selecione ao menos um grupo para exibir o gráfico.</p>
              ) : (
                <div className="h-64 sm:h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: isMobile ? 10 : 12, fill: '#64748b' }} interval={isMobile ? 1 : 0} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={kFormatter} />
                      <Tooltip content={<ChartTooltip />} />
                      {!isMobile && <Legend />}
                      {selectedChips.map(chip => (
                        <Line
                          key={chip.id}
                          type="monotone"
                          dataKey={history.labels[String(chip.id)] ?? String(chip.id)}
                          stroke={chip.color}
                          strokeWidth={2}
                          dot={{ r: 2 }}
                        />
                      ))}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )
        )}
      </div>
      )}

      {/* Projeção de cartões: parcelas × competências futuras, consolidadas
          por empresa (cartões da mesma empresa somados), obedecendo o filtro
          de empresa da tela. */}
      {mode === 'mensal' && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <CreditCardIcon size={18} className="text-emerald-600" />
            <h3 className="text-base font-semibold text-slate-800">Projeção de Cartões de Crédito</h3>
            <span className="text-xs text-slate-400">
              {/* O filtro de empresa fica longe, no topo — e é restaurado das
                  preferências sem aviso. Dizer o escopo aqui evita ler um
                  total parcial como se fosse o consolidado. */}
              {companyId == null
                ? 'todas as empresas — lançamentos já registrados nas próximas faturas'
                : `${companies.find(c => c.company_id === companyId)?.name ?? 'empresa selecionada'} — lançamentos já registrados nas próximas faturas`}
            </span>
          </div>
          {cardProjectionError && <ErrorBox message={cardProjectionError} onRetry={fetchCardProjection} />}
          {cardProjectionLoading && <LoadingSpinner />}
          {!cardProjectionLoading && !cardProjectionError && cardProjection && (
            <CreditCardProjectionMatrix
              data={cardProjection}
              companies={companies}
              view={{ kind: 'company', companyId }}
            />
          )}
        </div>
      )}

      {/* Modal de drill-down */}
      {drilldown && (
        <TransactionsModal
          year={year}
          month={month}
          companyId={companyId}
          erpId={drilldown.erpId}
          title={drilldown.title}
          basis={basis}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
};

export default ExpensesReportView;
